import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from './layout/TopBar';
import BottomNav from './BottomNav';
import Phone from './Phone';
import { C } from './theme';
import { fmtCurrency, getExportDateRangeLabel, useDrillAuth } from './drillHelpers';
import { CancelledShimmer, Bone } from './Shimmer';
import { useDesktopTopBar } from '../context/DesktopLayoutContext';
import { useIsDesktop } from './reports/reportTokens';
import { todayStr, yesterdayStr, getStartEndFromFilter } from '../services/reportsApi';
import SectionError from './reports/SectionError';
import SectionEmpty from './reports/SectionEmpty';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchLiveRefundDetailsRequest, fetchLiveRefundSummaryRequest, fetchLiveStaffRefundsRequest } from '../redux/reports/clickHouse/clickHouseActions';
import { getLiveRefundDetailsApi } from '../redux/reports/clickHouse/clickHouseApi';
import { formatMoneyForExport } from '../utils/currency';
import { ReportSectionExportButton } from './reports/components/ReportExportButton';
import type { LiveRefundDetailsItem, LiveStaffRefundsItem } from '../interface/clickHouseInterface';

// Refund ClickHouse rows come back with `orderDate: null` — the actual date sits in
// `voidedTime` ("2026-07-01 10:15:54"). Fall back to its date portion so the Order Log
// DATE column doesn't render blank/"—" for every row.
const refundRowDate = (o: any): string => {
    const raw = o?.orderDate ?? (typeof o?.voidedTime === 'string' ? o.voidedTime.split(/[ T]/)[0] : '');
    return raw || '';
};

type RefundSummaryRow = { cancelReason: string; orderCount: number; cancelledAmt?: number };
type RefundDetailsPagination = { page: number; totalPages: number; totalElements: number; pageSize: number; onPageChange: (page: number) => void; onRetry?: () => void };

// Refunds drill — same ClickHouse endpoints as the desktop drill for BOTH Today and
// historical date filters (historical requests carry startDate/endDate):
//   refund-summary → Cancel Reasons + totals, staff-refunds → Refunds by Staff,
//   refund-details → paginated Order Log.
// When embedded (onBack provided) the parent screen has already dispatched the fetches and
// passes the data via prefetched* props; standalone dispatches its own date-aware fetches.
const DrillCancelled = ({ onBack, initialDateFilter, initialDate, orderSource, prefetchedRefundSummary, refundSummaryLoading, refundSummaryFailure, prefetchedRefundDetails, refundDetailsLoading, refundDetailsFailure, refundDetailsPagination, prefetchedStaffRefunds, staffRefundsLoading, staffRefundsFailure }: { onBack?: () => void; initialDateFilter?: string; initialDate?: string; orderSource?: any; prefetchedRefundSummary?: RefundSummaryRow[]; refundSummaryLoading?: boolean; refundSummaryFailure?: boolean; prefetchedRefundDetails?: LiveRefundDetailsItem[]; refundDetailsLoading?: boolean; refundDetailsFailure?: boolean; refundDetailsPagination?: RefundDetailsPagination; prefetchedStaffRefunds?: LiveStaffRefundsItem[]; staffRefundsLoading?: boolean; staffRefundsFailure?: boolean } = {} as any) => {
    const isEmbedded = !!onBack;
    const navigate = useNavigate();
    const isDesktop = useIsDesktop();

    useEffect(() => {
        if (!isEmbedded && isDesktop) navigate('/reports/snapshot?drill=cancelled', { replace: true });
    }, [isDesktop, navigate, isEmbedded]);
    const setTopBarProps = useDesktopTopBar();
    const { locationId, selectedDate: drillDate, dateFilter: drillDateFilter, token } = useDrillAuth();
    const dispatch = useAppDispatch();
    const reduxRefundSummary = useAppSelector((s) => s.clickHouse.liveRefundSummarySuccess) as RefundSummaryRow[];
    const reduxRefundSummaryLoading = useAppSelector((s) => s.clickHouse.liveRefundSummaryLoading);
    const reduxRefundSummaryFailure = useAppSelector((s) => s.clickHouse.liveRefundSummaryFailure);
    const reduxStaffRefunds = useAppSelector((s) => s.clickHouse.liveStaffRefundsSuccess) as LiveStaffRefundsItem[];
    const reduxStaffRefundsLoading = useAppSelector((s) => s.clickHouse.liveStaffRefundsLoading);
    const reduxStaffRefundsFailure = useAppSelector((s) => s.clickHouse.liveStaffRefundsFailure);
    const reduxRefundDetails = useAppSelector((s) => s.clickHouse.liveRefundDetailsSuccess);
    const reduxRefundDetailsLoading = useAppSelector((s) => s.clickHouse.liveRefundDetailsLoading);
    const reduxRefundDetailsFailure = useAppSelector((s) => s.clickHouse.liveRefundDetailsFailure);
    const reduxRefundDetailsTotalPages = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalPages);
    const reduxRefundDetailsTotalElements = useAppSelector((s) => s.clickHouse.liveRefundDetailsTotalElements);
    const today = useMemo(() => todayStr(), []);
    const yesterday = useMemo(() => yesterdayStr(), []);
    const [selectedDate, setSelectedDate] = useState(initialDate ?? drillDate ?? today);
    const [activeDateFilter, setActiveDateFilter] = useState(initialDateFilter ?? drillDateFilter ?? 'Today');
    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDateFilter, today, yesterday);
        return range || { startDate: selectedDate, endDate: selectedDate };
    }, [activeDateFilter, selectedDate, today, yesterday]);

    const exportDateRangeLabel = getExportDateRangeLabel(activeDateFilter, dateRange);

    // Backend pagination for Order Log
    const ORDER_PAGE_SIZE = isDesktop ? 10 : 5;
    const [orderPage, setOrderPage] = useState(1);
    const [expandedReasons, setExpandedReasons] = useState<Set<number>>(new Set());

    const isToday = activeDateFilter === 'Today';
    const chStart = isToday ? undefined : dateRange.startDate;
    const chEnd = isToday ? undefined : dateRange.endDate;

    const handleDateFilterChange = useCallback((opt: string) => {
        setActiveDateFilter(opt);
        if (opt === 'Today') setSelectedDate(today);
        else if (opt === 'Yesterday') setSelectedDate(yesterday);
    }, [today, yesterday]);

    useEffect(() => {
        if (isEmbedded) return;
        setTopBarProps({
            title: 'Refund Orders',
            onBack: () => navigate('/reports/snapshot'),
            dateFilters: { value: activeDateFilter, onChange: handleDateFilterChange },
        });
    }, [setTopBarProps, activeDateFilter, handleDateFilterChange, navigate, isEmbedded]);

    // Standalone only: fetch summary + staff refunds for the selected range. When embedded,
    // the parent screen already dispatched these (with dates) and passes the results as props.
    useEffect(() => {
        if (isEmbedded || !locationId) return;
        dispatch(fetchLiveRefundSummaryRequest(locationId, chStart, chEnd));
        dispatch(fetchLiveStaffRefundsRequest({ locationId, startDate: chStart, endDate: chEnd }));
    }, [isEmbedded, locationId, chStart, chEnd, dispatch]);

    // Order Log — paginated refund-details. When the parent drives pagination
    // (refundDetailsPagination provided) it owns the fetch; skip to avoid a duplicate call.
    useEffect(() => {
        if (!locationId || refundDetailsPagination) return;
        dispatch(fetchLiveRefundDetailsRequest(locationId, orderPage, ORDER_PAGE_SIZE, undefined, chStart, chEnd));
    }, [locationId, refundDetailsPagination, orderPage, ORDER_PAGE_SIZE, chStart, chEnd, dispatch]);

    // Reset order log page whenever date filter changes
    useEffect(() => { setOrderPage(1); }, [activeDateFilter]);

    // Reset expanded reasons when page changes
    useEffect(() => { setExpandedReasons(new Set()); }, [orderPage]);

    const refundSummary = prefetchedRefundSummary ?? reduxRefundSummary;
    const summaryLoading = refundSummaryLoading ?? reduxRefundSummaryLoading;
    const summaryFailure = refundSummaryFailure ?? reduxRefundSummaryFailure;
    const staffRefunds = prefetchedStaffRefunds ?? reduxStaffRefunds;
    const staffLoading = staffRefundsLoading ?? reduxStaffRefundsLoading;
    const staffFailure = staffRefundsFailure ?? reduxStaffRefundsFailure;
    const refundDetails = prefetchedRefundDetails ?? reduxRefundDetails;
    const detailPageLoading = refundDetailsLoading ?? reduxRefundDetailsLoading;
    const detailPageFailure = refundDetailsFailure ?? reduxRefundDetailsFailure;
    const detailTotalPages = refundDetailsPagination?.totalPages ?? reduxRefundDetailsTotalPages;
    const detailTotalElements = refundDetailsPagination?.totalElements ?? reduxRefundDetailsTotalElements;
    const activeOrderPage = refundDetailsPagination?.page ?? orderPage;
    const setActiveOrderPage = refundDetailsPagination?.onPageChange ?? setOrderPage;
    const activePageSize = refundDetailsPagination?.pageSize ?? ORDER_PAGE_SIZE;

    const totalCount = refundSummary.reduce((s, r) => s + (Number(r.orderCount) || 0), 0);
    const totalAmount = refundSummary.reduce((s, r) => s + (Number(r.cancelledAmt) || 0), 0);
    const subTitle = totalCount > 0 || totalAmount > 0 ? `${totalCount} orders · ${fmtCurrency(totalAmount)} total` : 'No cancelled orders';
    const maxReasonCount = refundSummary.reduce((m, r) => Math.max(m, r.orderCount), 1);

    const sortedStaff = [...staffRefunds].sort((a, b) => b.refund - a.refund);
    const staffTotalAmt = staffRefunds.reduce((s, item) => s + (item.refund || 0), 0);

    const logRows = refundDetails.map((o: any, i: number) => ({
        id: (o.orderNo ?? o.orderNumber ?? o.orderId) != null ? `#${o.orderNo ?? o.orderNumber ?? o.orderId}` : `#${(activeOrderPage - 1) * activePageSize + i + 1}`,
        date: refundRowDate(o),
        amt: fmtCurrency(o.cancelledAmt ?? o.refundAmount ?? o.canceledAmount ?? o.cancelAmount ?? o.amount ?? 0),
        reason: o.cancelReason ?? o.reason ?? o.cancelledReason ?? o.refundReason ?? '-',
        staff: o.staffName ?? o.steward ?? o.staff ?? o.employeeName ?? o.cancelledBy ?? '-',
        ch: o.channel ?? o.channelName ?? o.orderChannel ?? o.orderSource ?? o.orderType ?? '',
        item: o.itemName ?? o.item ?? '',
    }));

    const pageLoading = summaryLoading && refundSummary.length === 0;

    const inner = pageLoading ? <CancelledShimmer /> : (<>
        {subTitle && <div style={{ fontSize: 10, color: C.t3, padding: '0 16px 4px' }}>{subTitle}</div>}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Cancel Reasons</div>
                    {summaryLoading ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ marginBottom: 8 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <Bone w={100} h={11} r={4} />
                                        <Bone w={24} h={11} r={4} />
                                    </div>
                                    <Bone w={null} h={5} r={3} />
                                </div>
                            ))}
                        </div>
                    ) : summaryFailure ? (
                        <SectionError kpiName="cancel reasons" onRetry={() => dispatch(fetchLiveRefundSummaryRequest(locationId, chStart, chEnd))} size="small" minHeight={80} />
                    ) : refundSummary.length ? (
                        refundSummary.map((r, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                    <span style={{ fontSize: 11, color: C.t2 }}>{r.cancelReason}</span>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: C.t1 }}>{r.orderCount}x</span>
                                </div>
                                <div style={{ height: 5, background: C.hover, borderRadius: 3, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${Math.min((r.orderCount / maxReasonCount) * 100, 100)}%`, background: C.red, borderRadius: 3, opacity: 0.6 }} />
                                </div>
                            </div>
                        ))
                    ) : (
                        <SectionEmpty kpiName="cancel reasons" size="small" minHeight={80} />
                    )}
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, boxShadow: C.shadow }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 4 }}>Refunds by Staff</div>
                    <div style={{ fontSize: 10, color: C.t3, marginBottom: 10 }}>Staff members who processed refunds</div>
                    {staffLoading ? (
                        <div>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: i < 3 ? `1px solid ${C.divider}` : 'none' }}>
                                    <div style={{ width: 20, height: 20, borderRadius: 6, background: C.hover, flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ height: 12, width: 90, background: C.hover, borderRadius: 4, marginBottom: 4 }} />
                                        <div style={{ height: 10, width: 60, background: C.hover, borderRadius: 4 }} />
                                    </div>
                                    <div style={{ height: 14, width: 36, background: C.hover, borderRadius: 4 }} />
                                </div>
                            ))}
                        </div>
                    ) : staffFailure ? (
                        <SectionError kpiName="refunds by staff" onRetry={() => dispatch(fetchLiveStaffRefundsRequest({ locationId, startDate: chStart, endDate: chEnd }))} size="small" minHeight={80} />
                    ) : sortedStaff.length ? (
                        <>
                            {sortedStaff.slice(0, 5).map((s, i) => {
                                const rate = staffTotalAmt > 0 ? (s.refund / staffTotalAmt) * 100 : 0;
                                return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: i < Math.min(sortedStaff.length, 5) - 1 ? `1px solid ${C.divider}` : 'none' }}>
                                        <div style={{ width: 20, height: 20, borderRadius: 6, background: i === 0 ? `${C.red}20` : C.hover, color: i === 0 ? C.red : C.t3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{i + 1}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.staff_name}</div>
                                            <div style={{ fontSize: 10, color: C.t3, marginTop: 1 }}>{s.orderCount} refunds · {rate.toFixed(1)}% rate</div>
                                        </div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: C.red, flexShrink: 0 }}>{fmtCurrency(s.refund)}</div>
                                    </div>
                                );
                            })}
                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.divider}`, display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.t3 }}>
                                <span>{sortedStaff.length} staff</span>
                                <span>Total: <span style={{ color: C.red, fontWeight: 600 }}>{fmtCurrency(staffTotalAmt)}</span></span>
                            </div>
                        </>
                    ) : (
                        <SectionEmpty kpiName="refunds by staff" size="small" minHeight={80} />
                    )}
                </div>
                <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 14, padding: 14, marginBottom: 12, boxShadow: C.shadow }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.t1, marginBottom: 10 }}>Order Log</div>
                        {logRows.length > 0 && (
                            <ReportSectionExportButton
                                dateRangeLabel={exportDateRangeLabel}
                                title="Refund Order Log"
                                filename="snapshot-refund-order-log"
                                buildExport={async (_format, opts) => {
                                    const targetLocationId = opts?.locationId ?? locationId;
                                    let total = detailTotalElements || 1;
                                    if (opts?.locationId) {
                                        const probe: any = await getLiveRefundDetailsApi({ locationId: targetLocationId, page: 1, size: 1, startDate: chStart, endDate: chEnd, orderSource, token });
                                        total = probe?.totalElements || 1;
                                    }
                                    const res: any = await getLiveRefundDetailsApi({ locationId: targetLocationId, page: 1, size: total, startDate: chStart, endDate: chEnd, orderSource, token });
                                    const list: any[] = Array.isArray(res?.content) ? res.content : (Array.isArray(res) ? res : []);
                                    return {
                                        headers: isToday
                                            ? ['Order', 'Staff', 'Reason', 'Channel', 'Amount']
                                            : ['Order', 'Date', 'Staff', 'Reason', 'Channel', 'Amount'],
                                        rows: list.map((o: any) => {
                                            const row: any[] = [o.orderNo ?? o.orderNumber ?? o.orderId ?? '—'];
                                            if (!isToday) row.push(refundRowDate(o) || '—');
                                            row.push(
                                                o.staffName ?? o.steward ?? o.staff ?? o.employeeName ?? o.cancelledBy ?? '-',
                                                o.cancelReason ?? o.reason ?? o.cancelledReason ?? o.refundReason ?? '-',
                                                o.channel ?? o.channelName ?? o.orderChannel ?? o.orderSource ?? o.orderType ?? '-',
                                                formatMoneyForExport(o.cancelledAmt ?? o.refundAmount ?? o.canceledAmount ?? o.cancelAmount ?? o.amount ?? 0),
                                            );
                                            return row;
                                        }),
                                    };
                                }}
                            />
                        )}
                    </div>
                    {detailPageLoading ? (
                        <div>
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} style={{ background: C.bg, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px', marginBottom: 5 }}>
                                    <Bone w={null} h={12} r={4} mb={6} />
                                    <Bone w={'60%' as any} h={10} r={4} />
                                </div>
                            ))}
                        </div>
                    ) : detailPageFailure ? (
                        <SectionError kpiName="refund order log" onRetry={refundDetailsPagination?.onRetry ?? (() => dispatch(fetchLiveRefundDetailsRequest(locationId, activeOrderPage, ORDER_PAGE_SIZE, undefined, chStart, chEnd)))} size="small" minHeight={80} />
                    ) : logRows.length ? (
                        <>
                            {logRows.map((o, i) => (
                                <div key={i} style={{ background: C.bg, border: `1px solid ${C.cardBorder}`, borderRadius: 10, padding: '10px 12px', marginBottom: 5 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: C.t1 }}>{o.id}</span>
                                            {!isToday && o.date && <span style={{ fontSize: 10, color: C.t3 }}>{o.date}</span>}
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: C.red }}>{o.amt}</span>
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: C.accent, marginBottom: 2 }}>{o.staff}</div>
                                    <div
                                        onClick={() => setExpandedReasons(prev => {
                                            const next = new Set(prev);
                                            if (next.has(i)) next.delete(i); else next.add(i);
                                            return next;
                                        })}
                                        style={{
                                            fontSize: 10, color: C.t2, marginBottom: 3,
                                            overflow: 'hidden',
                                            textOverflow: expandedReasons.has(i) ? undefined : 'ellipsis',
                                            whiteSpace: expandedReasons.has(i) ? 'normal' : 'nowrap',
                                            cursor: 'pointer',
                                        }}
                                    >{o.reason}</div>
                                    {o.item && <div style={{ fontSize: 10, color: C.t2, marginBottom: 3 }}>{o.item}</div>}
                                    {o.ch && o.ch !== '—' && o.ch !== '' && (
                                        <span style={{ fontSize: 9, color: C.t3, padding: '2px 6px', borderRadius: 4, background: C.hover }}>{o.ch}</span>
                                    )}
                                </div>
                            ))}
                            {detailTotalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                                    <div style={{ fontSize: 11, color: C.t3 }}>Page {activeOrderPage} of {detailTotalPages}</div>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                        <button
                                            onClick={() => setActiveOrderPage(Math.max(1, activeOrderPage - 1))}
                                            disabled={activeOrderPage === 1}
                                            style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: activeOrderPage === 1 ? C.hover : C.card, color: activeOrderPage === 1 ? C.t3 : C.t1, fontSize: 11, fontWeight: 600, cursor: activeOrderPage === 1 ? 'default' : 'pointer' }}
                                        >Prev</button>
                                        <span style={{ fontSize: 11, color: C.t2, padding: '4px 6px' }}>{activeOrderPage} / {detailTotalPages}</span>
                                        <button
                                            onClick={() => setActiveOrderPage(Math.min(detailTotalPages, activeOrderPage + 1))}
                                            disabled={activeOrderPage >= detailTotalPages}
                                            style={{ padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.cardBorder}`, background: activeOrderPage >= detailTotalPages ? C.hover : C.card, color: activeOrderPage >= detailTotalPages ? C.t3 : C.t1, fontSize: 11, fontWeight: 600, cursor: activeOrderPage >= detailTotalPages ? 'default' : 'pointer' }}
                                        >Next</button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <SectionEmpty kpiName="refund order log" size="small" minHeight={80} />
                    )}
                </div>
        </div>
    </>);

    if (isEmbedded) return <>{inner}</>;
    return (
        <Phone>
            <TopBar variant="mobile" />
            {inner}
            <BottomNav />
        </Phone>
    );
};

export default DrillCancelled;
